import { createContext, useContext, useEffect, useState } from 'react';
import { ChildrenProps } from '../../components/layout/Layout';
import { __ } from '../../i18n/l10n';
import useAccountAddress, {
  AddressType,
} from '../api/queries/useAccountAddress';
import usePlaceDetails from '../api/queries/usePlaceDetails';
import usePlaces, { PlacesResponse } from '../api/queries/usePlaces';
import useDebounce from '../utils/useDebounce';

interface IContext {
  accountAddress?: AddressType;
  address: PartialAddress;
  setAddress: (a: PartialAddress) => void;
  dataAddr?: PlacesResponse;
  dataZip?: PlacesResponse;
  dataCity?: PlacesResponse;
  placeId?: string;
  setPlaceId: (s: string) => void;
  addressSelected: AddressSelected;
  setAddressSelected: (a: AddressSelected) => void;
  addressOk: boolean;
  errors: Record<string, string[]>;
  setErrors: (err: Record<string, string[]>) => void;
}

const AddressContext = createContext<IContext | undefined>(undefined);

type PartialAddress = Omit<AddressType, 'firstname' | 'lastname'>;

export const initialAddress: PartialAddress = {
  address1: '',
  address2: '',
  zipcode: '',
  city: '',
  countryCode: '',
  mobilePhone: '',
  email: undefined,
};

type AddressSelected = 'WINA' | 'CUSTOM';

export const AddressContextProvider : React.FC<{
  children: React.ReactNode;
}> = ({ children }: ChildrenProps) => {
  const { data: accountAddress } = useAccountAddress();
  const [addressSelected, setAddressSelected] =
    useState<AddressSelected>('WINA');
  const [address, setAddress] = useState(initialAddress);
  const [placeId, setPlaceId] = useState<string>('');
  const [errors, setErrors] = useState({});
  const address1 = useDebounce(address.address1);
  const zipcode = useDebounce(address.zipcode);
  const city = useDebounce(address.city);

  const { data: dataAddr } = usePlaces(
    {
      query: `${address1}`,
      type: 'address',
    },
  );
  const { data: dataZip } = usePlaces(
    {
      query: zipcode,
      type: 'postcode',
    },
  );
  const { data: dataCity } = usePlaces({ query: city, type: 'city' });

  const { data: dataPlace, isError, isSuccess } = usePlaceDetails({ placeId });

  useEffect(() => {
    if(isError){
      setErrors({
        address1: [__('Please select an address in Europe')],
      });
    }

    if(isSuccess){
      if(dataPlace.city?.length) {
        setAddress((add) => ({
            ...add,
            city: dataPlace.city[0],
            countryCode: dataPlace.countryCode,
        }))
      }
      if(dataPlace.postcode?.length){
        setAddress((add) => ({
            ...add,
            zipcode: dataPlace.postcode[0],
            countryCode: dataPlace.countryCode,
        }));
      }
      setErrors({});
    }
  }, [isSuccess, isError, dataPlace]);

  const addressOk =
    dataAddr?.hits?.[0]?.locale_names?.[0] === address.address1 &&
    dataPlace?.city?.[0] === address.city &&
    dataPlace?.postcode?.[0] === address.zipcode;

  return (
    <AddressContext.Provider
      value={{
        accountAddress,
        address,
        setAddress,
        dataAddr,
        dataCity,
        dataZip,
        placeId,
        setPlaceId,
        addressSelected,
        setAddressSelected,
        addressOk,
        errors,
        setErrors,
      }}
    >
      {children}
    </AddressContext.Provider>
  );
};

export const useAddressContext = () => {
  const context = useContext(AddressContext);
  if (!context)
    throw new Error(
      'No AddressContext.Provider found when calling useAddressContext',
    );
  return context;
};
